// Presentation component for a collapsed run of executive-summary
// citations. Shows a short inline preview followed by a "+N" button that
// opens a popover listing every source in the run (including the ones
// already visible inline). The run is either grouped by segment (when the
// resolved citation records carry segment_name, as in the comparison
// pipeline) or rendered as a flat list (single-segment pipeline).

import { useCallback, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import Popover from '@mui/material/Popover';
import type { ExecutiveSummaryCitation } from '@trinity/chat/types/segment-executive-summary';
import {
  CITATION_INLINE_PREVIEW,
  CITATION_POPOVER_MAX_HEIGHT_VH,
  CITATION_POPOVER_WIDTH_PX,
} from './constants';

// Comparison citations extend the base shape with segment_name. We accept
// either shape and branch on presence of that field rather than a mode prop.
type MaybeComparisonCitation = ExecutiveSummaryCitation & { segment_name?: string };

interface Props {
  numbers: number[];
  citationsByNumber: Map<number, MaybeComparisonCitation>;
  onCitationClick: (n: number) => void;
}

export default function CitationRun({
  numbers,
  citationsByNumber,
  onCitationClick,
}: Props): React.JSX.Element {
  // Dedupe preserving order. If the upstream layer already deduped this is
  // a no-op; if it didn't, we guarantee the user-visible count is the
  // distinct count.
  const distinctNumbers = useMemo(() => {
    const seen = new Set<number>();
    const out: number[] = [];
    for (const n of numbers) {
      if (!seen.has(n)) {
        seen.add(n);
        out.push(n);
      }
    }
    return out;
  }, [numbers]);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const open = anchorEl != null;

  const handleOpen = useCallback(() => {
    if (triggerRef.current) setAnchorEl(triggerRef.current);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    // Focus is restored automatically by MUI Popover via restoreFocus.
  }, []);

  const handleChipActivation = (n: number) => {
    handleClose();
    onCitationClick(n);
  };

  const previewCount = Math.min(CITATION_INLINE_PREVIEW, distinctNumbers.length);
  const hiddenCount = distinctNumbers.length - previewCount;

  return (
    <>
      {distinctNumbers.slice(0, previewCount).map((n) => (
        <InlineChip
          key={n}
          type="button"
          onClick={() => onCitationClick(n)}
          aria-label={`Citation ${n}`}
        >
          [{n}]
        </InlineChip>
      ))}
      <MoreTrigger
        ref={triggerRef}
        type="button"
        onClick={handleOpen}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Show all ${distinctNumbers.length} citations for this claim`}
      >
        {hiddenCount} {hiddenCount === 1 ? 'source' : 'sources'}
      </MoreTrigger>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        disableRestoreFocus={false}
        slotProps={{
          paper: {
            sx: {
              width: CITATION_POPOVER_WIDTH_PX,
              maxHeight: `${CITATION_POPOVER_MAX_HEIGHT_VH}vh`,
            },
          },
        }}
      >
        <PopoverContent role="dialog" aria-label="Citations for this claim">
          <PopoverHeader>
            Sources ({distinctNumbers.length})
          </PopoverHeader>
          <RowList>
            <CitationRows
              numbers={distinctNumbers}
              citationsByNumber={citationsByNumber}
              onClickRow={handleChipActivation}
            />
          </RowList>
        </PopoverContent>
      </Popover>
    </>
  );
}

interface RowsProps {
  numbers: number[];
  citationsByNumber: Map<number, MaybeComparisonCitation>;
  onClickRow: (n: number) => void;
}

function CitationRows({ numbers, citationsByNumber, onClickRow }: RowsProps): React.JSX.Element {
  const resolved = numbers
    .map((n) => citationsByNumber.get(n))
    .filter((c): c is MaybeComparisonCitation => c != null);

  const hasSegmentMetadata = resolved.some((c) => typeof c.segment_name === 'string' && c.segment_name.length > 0);

  if (!hasSegmentMetadata) {
    // Single-segment view: flat list.
    return (
      <>
        {resolved.map((c) => (
          <CitationRow key={c.number} citation={c} onClick={() => onClickRow(c.number)} />
        ))}
      </>
    );
  }

  // Comparison view: group by segment, preserving the first-seen segment
  // order so the group layout mirrors how the user selected the segments.
  const groups: Array<{ segmentName: string; citations: MaybeComparisonCitation[] }> = [];
  const indexBySegment = new Map<string, number>();
  for (const c of resolved) {
    const name = c.segment_name ?? 'Unknown';
    let idx = indexBySegment.get(name);
    if (idx == null) {
      idx = groups.length;
      indexBySegment.set(name, idx);
      groups.push({ segmentName: name, citations: [] });
    }
    groups[idx].citations.push(c);
  }

  return (
    <>
      {groups.map((group) => (
        <Group key={group.segmentName}>
          <GroupHeader>{group.segmentName}</GroupHeader>
          {group.citations.map((c) => (
            <CitationRow key={c.number} citation={c} onClick={() => onClickRow(c.number)} />
          ))}
        </Group>
      ))}
    </>
  );
}

function CitationRow({
  citation,
  onClick,
}: {
  citation: MaybeComparisonCitation;
  onClick: () => void;
}): React.JSX.Element {
  return (
    <Row type="button" onClick={onClick}>
      <RowHeader>
        <RowNumber>[{citation.number}]</RowNumber>
        <RowTitle>{citation.source_title}</RowTitle>
        <RowTypeBadge $type={citation.source_type}>{citation.source_type}</RowTypeBadge>
      </RowHeader>
      {citation.source_subtitle && <RowSubtitle>{citation.source_subtitle}</RowSubtitle>}
    </Row>
  );
}

const chipShared = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 18px;
  padding: 1px 6px;
  margin: 0 2px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.4;
  vertical-align: baseline;
  cursor: pointer;
  transition: background 120ms ease;
  border: none;
`;

const InlineChip = styled.button`
  ${chipShared};
  background: #dbe4f5;
  color: #4a6bb0;
  &:hover {
    background: #c3d2ee;
  }
  &:focus-visible {
    outline: 2px solid rgba(74, 107, 176, 0.5);
    outline-offset: 1px;
  }
`;

// Matches InlineChip so the rollup chip reads as part of the same citation
// run rather than a separate control. Extra horizontal padding gives the
// "N sources" text breathing room without affecting single-number chips.
const MoreTrigger = styled.button`
  ${chipShared};
  padding: 1px 8px;
  background: #dbe4f5;
  color: #4a6bb0;
  &:hover {
    background: #c3d2ee;
  }
  &:focus-visible {
    outline: 2px solid rgba(74, 107, 176, 0.5);
    outline-offset: 1px;
  }
`;

const PopoverContent = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const PopoverHeader = styled.div`
  padding: 10px 14px 8px;
  font-size: 12px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.55);
  text-transform: uppercase;
  letter-spacing: 0.4px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
`;

const RowList = styled.div`
  display: flex;
  flex-direction: column;
  padding: 6px 8px 8px;
  gap: 4px;
  overflow-y: auto;
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 4px;
  &:first-of-type {
    margin-top: 0;
  }
`;

const GroupHeader = styled.div`
  padding: 6px 6px 2px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #626262;
`;

const Row = styled.button`
  all: unset;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 10px;
  border-radius: 6px;
  cursor: pointer;
  &:hover {
    background: #f4f7fb;
  }
  &:focus-visible {
    background: #f0f5fc;
    box-shadow: 0 0 0 2px rgba(74, 107, 176, 0.25);
  }
`;

const RowHeader = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
`;

const RowNumber = styled.span`
  color: #4a6bb0;
  font-weight: 700;
  font-size: 12px;
  flex-shrink: 0;
`;

const RowTitle = styled.span`
  color: #323232;
  font-weight: 600;
  font-size: 12px;
  overflow-wrap: anywhere;
`;

const RowTypeBadge = styled.span<{ $type: 'card' | 'chart' }>`
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  padding: 2px 5px;
  border-radius: 3px;
  background: ${(p) => (p.$type === 'chart' ? '#e4ecf7' : '#f0ebf7')};
  color: ${(p) => (p.$type === 'chart' ? '#2f548c' : '#6a4c93')};
  flex-shrink: 0;
  margin-left: auto;
`;

const RowSubtitle = styled.span`
  font-size: 11px;
  line-height: 1.4;
  color: rgba(0, 0, 0, 0.6);
`;
